import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { IconButton, cx } from '../../components/ui.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { useToast } from '../../components/Toast.tsx';
import type { MenuItem } from '../../components/ContextMenu.tsx';

/**
 * The star, and the one line of plumbing behind it.
 *
 * Shared by every row and tile in the Drive because the behaviour has to be
 * identical everywhere: a star set on a sheet in the list view is the same
 * star seen on the Starred page a second later, and that only stays true while
 * there is one place that sets it and one place that says what to refresh.
 *
 * Optimism deliberately left out. A star is one small request and the list it
 * feeds is the point of the feature — showing it filled before the server has
 * agreed would mean a page that disagrees with itself after a refresh, which
 * is the one thing a bookmark must never do.
 */

export type StarKind = 'file' | 'folder' | 'spreadsheet' | 'document';

export function useStar(kind: StarKind, id: string, name: string) {
  const queries = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (isStarred: boolean) => api.post(`/api/drive/starred/${kind}/${id}`, { isStarred }),
    onSuccess: (_result, isStarred) => {
      /*
       * Said out loud, because the star may not be on screen.
       *
       * Starring from the menu leaves nothing visibly different in a grid of
       * forty tiles — the row you acted on is behind the menu that closed —
       * and a bookmark you are not sure you set is a bookmark you set twice.
       */
      toast.success(
        isStarred ? `${name} added to Starred` : `${name} removed from Starred`,
        isStarred ? 'Find it under Starred in the sidebar.' : undefined,
      );

      // The listing it came from, and the list it belongs to.
      void queries.invalidateQueries({ queryKey: ['drive'] });
      void queries.invalidateQueries({ queryKey: ['starred'] });
      void queries.invalidateQueries({ queryKey: ['spreadsheets'] });
      void queries.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * The same act, as a line in a menu.
 *
 * The hover star is quick and invisible until you hover, which is fine for a
 * mouse on a row and no use at all when somebody has opened the menu to look
 * for the command — that is where people go to find out what they can do. So
 * it is in both places, and both say the same thing because both are this.
 */
export function useStarMenuItem(
  kind: StarKind,
  id: string,
  name: string,
  starred: boolean,
): MenuItem {
  const star = useStar(kind, id, name);

  return {
    label: starred ? 'Remove from Starred' : 'Add to Starred',
    icon: <StarGlyph filled={starred} />,
    onSelect: () => star.mutate(!starred),
  };
}

/**
 * The star on a row.
 *
 * Visible when it is on, and on hover when it is off — Drive's own rule, and
 * the reason a listing of forty files is not forty grey stars.
 */
export function StarButton({
  kind,
  id,
  name,
  starred,
}: {
  kind: StarKind;
  id: string;
  name: string;
  starred: boolean;
}) {
  const star = useStar(kind, id, name);

  return (
    <Tooltip label={starred ? 'Remove the star' : 'Star this'}>
      <span
        className={cx(
          'transition-fast',
          starred ? 'opacity-100' : 'opacity-0 focus-within:opacity-100 group-hover:opacity-100',
        )}
      >
        <IconButton
          label={starred ? `Remove the star from ${name}` : `Star ${name}`}
          onClick={() => star.mutate(!starred)}
          disabled={star.isPending}
        >
          <StarGlyph filled={starred} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

/** Filled when it is on, outlined when it is not. */
export function StarGlyph({ filled = false }: { filled?: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden
      fill={filled ? 'var(--amber-500, #f59e0b)' : 'none'}
      stroke={filled ? 'var(--amber-500, #f59e0b)' : 'currentColor'}
      strokeWidth="1.5"
      strokeLinejoin="round"
    >
      <path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
    </svg>
  );
}
